import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import clinicalReportsService from '../../../services/clinicalReportsService';
import CompanyService from '../../../services/companyService';
import { useDoctorWorkspace } from '../../doctor/doctorWorkspaceContext';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import Icon from '../../common/Icons';
import { HeadDoctorPageHeader } from '../ui';
import { todayYmd, daysAgoYmd } from '../../../utils/appTimezone';
import ReportsFilterBar from './ReportsFilterBar';
import ReportsSubNav from './ReportsSubNav';
import { ClinicalReportsContext } from '../../clinical-reports/ClinicalReportsContext';
import { REPORTS_PERIOD_PRESETS } from './reportsNavConfig';
import '../HeadDoctorDashboard.css';
import './charts/chartTheme';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

function detectActivePreset(from, to) {
  const today = todayYmd();
  if (to !== today) return null;
  const match = REPORTS_PERIOD_PRESETS.find((preset) => from === daysAgoYmd(preset.days));
  return match?.key || null;
}

export default function HeadDoctorReportsLayout() {
  const { workSiteId, workSites } = useDoctorWorkspace();
  const [from, setFrom] = useState(daysAgoYmd(30));
  const [to, setTo] = useState(todayYmd());
  const [siteFilter, setSiteFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (workSiteId) setSiteFilter(String(workSiteId));
  }, [workSiteId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await CompanyService.getActiveCompanies();
        if (!cancelled && res.success) {
          setCompanies(Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) setCompanies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activePreset = useMemo(() => detectActivePreset(from, to), [from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters = { from, to, limit: 20 };
      if (siteFilter) filters.site_id = parseInt(siteFilter, 10);
      if (companyFilter) filters.company_id = parseInt(companyFilter, 10);
      const res = await clinicalReportsService.getOverview(filters);
      if (res.success) {
        setData(res.data);
      } else {
        setData(null);
        setError(COPY.ERROR_LOAD);
      }
    } catch {
      setData(null);
      setError(COPY.ERROR_LOAD);
    } finally {
      setLoading(false);
    }
  }, [from, to, siteFilter, companyFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePreset = useCallback((preset) => {
    setTo(todayYmd());
    setFrom(daysAgoYmd(preset.days));
  }, []);

  const siteLabel = useMemo(() => {
    if (!siteFilter) return COPY.FILTER_ALL_SITES;
    const site = (workSites || []).find((s) => String(s.id) === String(siteFilter));
    return site?.name || data?.filters?.site_name || COPY.FILTER_SITE;
  }, [siteFilter, workSites, data?.filters?.site_name]);

  const companyLabel = useMemo(() => {
    if (!companyFilter) return COPY.FILTER_ALL_COMPANIES;
    const company = companies.find((c) => String(c.id) === String(companyFilter));
    return company?.name || data?.filters?.company_name || COPY.FILTER_COMPANY;
  }, [companyFilter, companies, data?.filters?.company_name]);

  const periodLabel = data?.range
    ? `${data.range.from} → ${data.range.to}`
    : `${from} → ${to}`;

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      summary: data?.summary || {},
      copy: COPY,
      refresh: load,
      siteLabel,
      companyLabel,
      periodLabel,
      reportFilters: {
        from,
        to,
        site_id: siteFilter ? parseInt(siteFilter, 10) : null,
        company_id: companyFilter ? parseInt(companyFilter, 10) : null,
        limit: 10000
      }
    }),
    [
      data,
      loading,
      error,
      load,
      siteLabel,
      companyLabel,
      periodLabel,
      from,
      to,
      siteFilter,
      companyFilter
    ]
  );

  return (
    <ClinicalReportsContext.Provider value={value}>
      <div className="hd-reports-page">
        <HeadDoctorPageHeader
          badge={COPY.BADGE}
          title={COPY.TITLE}
          description={COPY.DESCRIPTION}
        />

        <section
          className={`hd-reports-shell mb-4${loading ? ' is-loading' : ''}`}
          aria-busy={loading}
        >
          <ReportsFilterBar
            from={from}
            to={to}
            siteFilter={siteFilter}
            companyFilter={companyFilter}
            workSites={workSites}
            companies={companies}
            loading={loading}
            activePreset={activePreset}
            onFromChange={setFrom}
            onToChange={setTo}
            onSiteChange={setSiteFilter}
            onCompanyChange={setCompanyFilter}
            onPreset={handlePreset}
            onRefresh={load}
          />

          <div className="hd-reports-context">
            <span className="hd-reports-context__label">{COPY.CONTEXT_PREFIX}</span>
            <div className="hd-reports-context__chips">
              <span className="hd-reports-context-chip hd-reports-context-chip--site">
                <Icon name="building" size={14} aria-hidden />
                <strong>{siteLabel}</strong>
              </span>
              <span className="hd-reports-context-chip hd-reports-context-chip--company">
                <Icon name="users" size={14} aria-hidden />
                <strong>{companyLabel}</strong>
              </span>
              <span className="hd-reports-context-chip hd-reports-context-chip--period">
                <Icon name="calendar" size={14} aria-hidden />
                {periodLabel}
              </span>
              {loading ? (
                <span className="hd-reports-context-chip hd-reports-context-chip--loading">
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  {COPY.LOADING_OVERLAY}
                </span>
              ) : null}
            </div>
          </div>

          <ReportsSubNav />
        </section>

        {error ? (
          <Alert variant="danger" className="mb-3 hd-reports-alert">
            {error}
          </Alert>
        ) : null}

        <div className="hd-reports-outlet">
          <Outlet />
        </div>
      </div>
    </ClinicalReportsContext.Provider>
  );
}
